import { Link, useLocation, Navigate } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import { useStartProject } from "@/lib/startProjectContext";
import { getCategoryHub } from "@/data/categoryHubs";
import { getHomeDecorCategory } from "@/data/homeDecor";
import { stripLocale } from "@/lib/i18n";

/**
 * Sprint 30 · Top-level category hub. SEO-ready landing page that aggregates
 * the Sprint-28 home-décor subcategory pages (and selected /products/* pages
 * where relevant) under one unique H1, one primary search intent and one
 * dedicated FAQ block. No duplicate content — the hub body is 100% new copy.
 */
export const CategoryHub = () => {
  const location = useLocation();
  const canonicalPath = stripLocale(location.pathname);
  const id = canonicalPath.replace(/^\/categories\//, "").replace(/\/$/, "");
  const hub = getCategoryHub(id);
  const { open: openProject } = useStartProject();

  const path = hub ? `/categories/${hub.id}` : "/categories";
  const canonical = `${SITE_URL}${path}`;

  useSeo({
    title: hub ? hub.title : "Category hub",
    description: hub ? hub.metaDescription : undefined,
    path,
    canonical,
    image: hub ? hub.hero : undefined,
    type: "website",
    jsonLd: hub
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              "@id": `${canonical}#collection`,
              name: hub.h1,
              description: hub.metaDescription,
              url: canonical,
              inLanguage: "en",
              about: hub.primaryKeyword,
              provider: { "@id": `${SITE_URL}#organization` },
            },
            {
              "@type": "FAQPage",
              mainEntity: hub.buyerQuestions.map(([q, a]) => ({
                "@type": "Question",
                name: q,
                acceptedAnswer: { "@type": "Answer", text: a },
              })),
            },
            breadcrumb([
              { name: "Home", path: "/" },
              { name: "Categories", path: "/categories" },
              { name: hub.h1, path },
            ]),
          ],
        }
      : null,
    jsonLdId: `category-hub-${id}`,
  });

  if (!hub) return <Navigate to="/" replace />;

  const subs = hub.subcategorySlugs
    .map((slug) => ({ slug, page: getHomeDecorCategory(slug) }))
    .filter((x) => x.page);

  return (
    <article data-testid={`category-hub-${hub.id}`}>
      {/* HERO */}
      <header
        style={{
          position: "relative",
          minHeight: "68svh",
          background: "var(--ink)",
          color: "var(--paper)",
          display: "flex",
          alignItems: "flex-end",
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url("${hub.hero}")`,
            backgroundSize: "cover",
            backgroundPosition: "center 42%",
            filter: "grayscale(.08) brightness(.5) contrast(1.15)",
            transform: "scale(1.06)",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(26,23,20,.3) 0%, rgba(26,23,20,.55) 55%, rgba(26,23,20,.97) 100%), linear-gradient(90deg, rgba(26,23,20,.88) 0%, rgba(26,23,20,.32) 60%, rgba(26,23,20,0) 100%)",
          }}
        />
        <div
          className="wrap"
          style={{
            position: "relative",
            zIndex: 2,
            paddingBlock: "calc(74px + var(--section)) clamp(3rem, 7vw, 6rem)",
            width: "100%",
          }}
        >
          <Reveal>
            <p className="eyebrow eyebrow-light" data-testid="hub-eyebrow">{hub.eyebrow}</p>
          </Reveal>
          <WordReveal as="h1" text={hub.h1} className="display" stagger={60} testId="hub-headline" />
          <Reveal as="p" delay={150} style={{ maxWidth: "60ch", color: "rgba(239,234,224,.78)", marginTop: "1.4rem", fontSize: "1.15rem", fontFamily: "var(--display)", fontStyle: "italic", fontWeight: 340 }}>
            {hub.lede}
          </Reveal>
          <Reveal delay={300} style={{ marginTop: "2rem", display: "flex", flexWrap: "wrap", gap: ".8rem" }}>
            <button
              type="button"
              onClick={openProject}
              className="btn btn-fill btn-lg btn-glow"
              data-testid={`hub-${hub.id}-cta`}
            >
              <span>Start a {hub.h1.replace(/\.$/, "").toLowerCase()} programme →</span>
            </button>
          </Reveal>
        </div>
      </header>

      {/* OVERVIEW */}
      {hub.overview.map((sec, i) => (
        <section
          key={i}
          className="wrap"
          style={{ paddingBlock: "var(--section)", borderTop: i === 0 ? "none" : "1px solid var(--line)" }}
          data-testid={`hub-${hub.id}-overview-${i}`}
        >
          <Reveal as="p" className="eyebrow">Overview</Reveal>
          <Reveal as="h2" className="display" style={{ fontSize: "var(--t-h1)", margin: "1rem 0 1.4rem", lineHeight: 1.05 }}>
            {sec.h2}
          </Reveal>
          {sec.body.map((p, j) => (
            <Reveal as="p" key={j} style={{ color: "var(--ink-soft)", maxWidth: "72ch", fontSize: "1.05rem", marginTop: j === 0 ? 0 : "1rem" }}>
              {p}
            </Reveal>
          ))}
        </section>
      ))}

      {/* SUBCATEGORY DIRECTORY */}
      <section
        className="wrap hd-families"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
        data-testid={`hub-${hub.id}-subcategories`}
      >
        <Reveal as="p" className="eyebrow">Sub-categories</Reveal>
        <Reveal as="h2" className="display" style={{ fontSize: "var(--t-h1)", margin: "1rem 0 1.4rem", lineHeight: 1.05 }}>
          Browse the {hub.h1.replace(/\.$/, "").toLowerCase()} library.
        </Reveal>
        <div className="hd-family-grid" style={{ marginTop: "1.5rem" }}>
          {subs.map(({ slug, page }) => (
            <Reveal key={slug}>
              <Link to={`/categories/home-decor/${slug}`} className="hd-card" data-testid={`hub-${hub.id}-sub-${slug}`}>
                <span className="hd-card-kw">{page.primaryKeyword}</span>
                <span className="hd-card-name">
                  {slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
                </span>
                <span className="hd-card-arrow">Explore →</span>
              </Link>
            </Reveal>
          ))}
          {(hub.extraInternal || []).map((link) => (
            <Reveal key={link.to}>
              <Link to={link.to} className="hd-card" data-testid={`hub-${hub.id}-extra-${link.to.split("/").pop()}`}>
                <span className="hd-card-kw">Related product</span>
                <span className="hd-card-name">{link.label}</span>
                <span className="hd-card-arrow">Explore →</span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)", background: "var(--paper-raised)" }}
        data-testid={`hub-${hub.id}-services`}
      >
        <Reveal as="p" className="eyebrow">Sourcing services</Reveal>
        <Reveal as="h2" className="display" style={{ fontSize: "var(--t-h1)", margin: "1rem 0 1.4rem", lineHeight: 1.05 }}>
          Every programme runs the full Asia Sourcing stack.
        </Reveal>
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1rem" }}>
          {hub.serviceLinks.map((s) => (
            <Reveal as="li" key={s.to}>
              <Link to={s.to} className="hd-card" data-testid={`hub-${hub.id}-svc-${s.to.split("/").pop()}`}>
                <span className="hd-card-kw">Service</span>
                <span className="hd-card-name">{s.label}</span>
                <span className="hd-card-arrow">See service →</span>
              </Link>
            </Reveal>
          ))}
        </ul>
      </section>

      {/* FAQ */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
        data-testid={`hub-${hub.id}-faq`}
      >
        <Reveal as="p" className="eyebrow">Buyer FAQ</Reveal>
        <Reveal as="h2" className="display" style={{ fontSize: "var(--t-h1)", margin: "1rem 0 1.4rem", lineHeight: 1.05 }}>
          What international buyers ask us.
        </Reveal>
        <div style={{ marginTop: "1rem" }}>
          {hub.buyerQuestions.map(([q, a], i) => (
            <details key={i} style={{ padding: "1rem 0", borderBottom: "1px solid var(--line)" }} data-testid={`hub-${hub.id}-faq-${i}`}>
              <summary style={{ cursor: "pointer", fontWeight: 500, fontFamily: "var(--display)", fontSize: "1.1rem" }}>{q}</summary>
              <p style={{ marginTop: ".8rem", color: "var(--ink-soft)", maxWidth: "72ch" }}>{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="cta wrap" style={{ borderTop: "1px solid var(--line)" }}>
        <Reveal as="p" className="eyebrow">Start an enquiry</Reveal>
        <Reveal as="h2" className="display">
          Start a {hub.h1.replace(/\.$/, "").toLowerCase()} programme.
        </Reveal>
        <Reveal className="row">
          <button
            type="button"
            onClick={openProject}
            className="btn btn-fill btn-lg btn-glow"
            data-testid={`hub-${hub.id}-cta-bottom`}
          >
            <span>Send a brief →</span>
          </button>
          <Link to="/clusters" className="btn btn-ghost">See all clusters</Link>
        </Reveal>
      </section>
    </article>
  );
};

export default CategoryHub;
